'use client';

import { Dispatch, SetStateAction, useState } from 'react';
import { useForm } from 'react-hook-form';
import { DallasOptionItem } from '../Dallas/Forms/DallasOptionItem/DallasOptionItem';
import DallasButton from '../Dallas/Buttons/DallasButton/DallasButton';
import DallasStack from '../Dallas/Stack/DallasStack';
import DallasForm from '../Dallas/Forms/DallasForm';
import DallasBanner from '../Dallas/Banner/DallasBanner';
import DallasSelectField from '../Dallas/Forms/DallasSelectField/DallasSelectField';
import DallasTextArea from '../Dallas/Forms/DallasTextArea/DallasTextArea';
import DallasSvgIconWarningOutlined from '../Dallas/Icons/DallasSvgIconWarningOutlined';
import { getErrorDisplayMessageOrDefault } from '@lib/api/error-response';
import DallasSpinner from '../Dallas/ProgressIndicators/Spinner/DallasSpinner';

export interface CreateSupportTicketFormValues<T> {
  category: T | null;
  description: string;
}

interface Props<T> {
  onClose: () => void;
  setIsSubmittedSuccessful: Dispatch<SetStateAction<boolean>>;
  createSupportTicket: (
    category: T,
    description: string,
  ) => Promise<{ isSuccess: boolean }>;
  options: DallasOptionItem<T>[];
}

export default function CreateSupportTicketDialogFormContent<T extends string>({
  onClose,
  setIsSubmittedSuccessful,
  createSupportTicket,
  options,
}: Props<T>) {
  const [errorMessage, setErrorMessage] = useState('');

  const form = useForm<CreateSupportTicketFormValues<T>>({
    defaultValues: {
      category: null,
      description: '',
    },
  });
  const {
    formState: { isValid, isSubmitting },
  } = form;

  return (
    <DallasForm<CreateSupportTicketFormValues<T>>
      form={form}
      onSubmit={async ({ category, description }) => {
        if (category != null) {
          try {
            setErrorMessage('');
            await createSupportTicket(category, description);
            setIsSubmittedSuccessful(true);
          } catch (error) {
            setErrorMessage(getErrorDisplayMessageOrDefault(error));
          }
        }
      }}
    >
      <DallasStack gap="2xl">
        <DallasStack gap="lg">
          {!!errorMessage && (
            <DallasBanner
              addOnStart={<DallasSvgIconWarningOutlined />}
              text={errorMessage}
              color="error"
            />
          )}
          <DallasSelectField
            name="category"
            label="What can we help you with?"
            placeholder="Select a category"
            options={options}
            disabled={isSubmitting}
            withModalPopover
            required
          />
        </DallasStack>
        <DallasTextArea
          name="description"
          label="Add comments"
          placeholder="Please describe your issue in detail"
          disabled={isSubmitting}
          rows={5}
          required
        />
        <DallasStack gap="xs" alignHorizontal="center">
          <DallasButton
            label={isSubmitting ? '' : 'Submit ticket'}
            addOnStart={isSubmitting ? <DallasSpinner /> : undefined}
            color="primary"
            disabled={!isValid || isSubmitting}
            submit
          />
          <DallasButton label="Cancel" onClick={onClose} color="tertiary" />
        </DallasStack>
      </DallasStack>
    </DallasForm>
  );
}
